import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { Table } from "app/components";

import AutoComplete from "@jumbo/components/JumboAutoComplete";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import Button from "@jumbo/components/JumboLoadingButton";
import { CASH, CASH_BOOK, CHEQUE_BOOK } from "app/routes/constant";

import {
  transactionBy,
  transactionType,
} from "@jumbo/constants/transactionConst";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useSnackbar } from "notistack";
import { createEntryBook } from "app/redux/slice/entrybookSlice";
import { getExpenseTypeList } from "app/redux/slice/expenseTypeSlice";
import { getVendorList } from "app/redux/slice/vendorSlice";
import { getBillList } from "app/redux/slice/billSlice";
import { getPlatformList } from "app/redux/slice/platformSlice";
import { getSalesInvoiceList } from "app/redux/slice/salesInvoiceSlice";
import { getIncomeTypeList } from "app/redux/slice/incomeTypeSlice";

const useStyles = makeStyles({
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const CreditAndDebit = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { cash, cheque } = transactionBy;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const vendorData = useSelector((state) => state?.vendor);
  const { search, pathname } = useLocation();
  const platformData = useSelector((state) => state?.platform);

  const searchParams = new URLSearchParams(search);
  const incomeTypeData = useSelector((state) => state?.incomeType);

  const expenseTypeData = useSelector((state) => state?.expenseType);
  const isDebit = searchParams.get("type");

  const isCredit = isDebit === transactionType?.credit.toLocaleLowerCase();
  const [invoiceData, setInvoiceData] = useState([]);
  const [billData, setBillData] = useState([]);

  const [type, setType] = useState(
    isDebit === transactionType?.debit.toLocaleLowerCase()
      ? "expense"
      : "platform"
  );

  const errorMsg = (error) => {
    enqueueSnackbar(error, { variant: "error" });
  };
  let baseSchema = {
    amount: yup
      .number()
      .typeError("Amount must be a number.")
      .required("Please enter amount.")
      .min(1, "Amount must be greater than zero."),
    transaction_date: yup.date().required("Please select transaction date."),
  };

  if (isCredit) {
    if (type === "platform") {
      baseSchema.platform = yup
        .object()
        .nullable()
        .required("Please select platform.")
        .shape({
          _id: yup.string().required(),
        });
    } else {
      if (type === "party") {
        baseSchema.incomeType = yup
          .object()
          .nullable()
          .required("Please select income type.")
          .shape({
            _id: yup.string().required(),
          });
        baseSchema.description = yup
          .string()
          .required("Please enter description.");
      }
      baseSchema.vendor = yup
        .object()
        .nullable()
        .required("Please select vendor.")
        .shape({
          _id: yup.string().required(),
        });
    }
  } else {
    if (type === "bill") {
      baseSchema.vendor = yup
        .object()
        .nullable()
        .required("Please select vendor.")
        .shape({
          _id: yup.string().required(),
        });
    } else {
      baseSchema.expenseType = yup
        .object()
        .nullable()
        .required("Please select expense type.")
        .shape({
          _id: yup.string().required(),
        });
      baseSchema.description = yup
        .string()
        .required("Please enter description.");
    }
  }

  const validationSchema = yup.object().shape(baseSchema);

  const handleReceiveAmountChange = (index, newValue, max) => {
    if (newValue > max) newValue = max;
    const updatedDataArray = [...invoiceData];
    const updatedItem = { ...updatedDataArray[index], creditAmount: +newValue };
    updatedDataArray[index] = updatedItem;
    setInvoiceData(updatedDataArray);
  };

  const handlePaidAmountChange = (index, newValue, max) => {
    if (newValue > max) newValue = max;
    const updatedDataArray = [...billData];
    const updatedItem = { ...updatedDataArray[index], debitAmount: +newValue };
    updatedDataArray[index] = updatedItem;
    setBillData(updatedDataArray);
  };

  const column = [
    {
      label: "Sr No.",
      render: (elm, i) => i + 1,
    },
    { id: "billNo", label: "Bill No." },
    {
      id: "billDate",
      label: "Bill Date",
      render: (elm) => <> {moment(elm?.billDate).format("DD/MM/YYYY")}</>,
    },
    { id: "totalAmount", label: "Bill Amount" },
    { id: "pendingAmount", label: "Pending Amount" },
    {
      label: "Amount Received",
      minWidth: 130,
      render: (elm, i) => (
        <TextField
          type="number"
          size="small"
          value={elm.debitAmount ?? elm.pendingAmount}
          InputProps={{
            inputProps: {
              max: elm.pendingAmount,
              min: 0,
            },
          }}
          onChange={(e) =>
            handlePaidAmountChange(i, e.target.value, elm.pendingAmount)
          }
        />
      ),
    },
    {
      label: "Due Amount",
      render: (elm) => {
        return elm.pendingAmount - (elm.debitAmount ?? elm.pendingAmount);
      },
    },
  ];

  const columnInvoice = [
    {
      label: "Sr No.",
      render: (elm, i) => i + 1,
    },
    { id: "invoiceNumber", label: "Invoice No." },
    {
      id: "invoiceDate",
      label: "Invoice Date",
      render: (elm) => <> {moment(elm?.invoiceDate).format("DD/MM/YYYY")}</>,
    },
    { id: "totalAmount", label: "Invoice Amount" },
    { id: "dueAmount", label: "Pending Amount" },
    {
      label: "Amount Received",
      minWidth: 130,
      render: (elm, i) => (
        <TextField
          type="number"
          size="small"
          value={elm.creditAmount ?? elm.dueAmount}
          InputProps={{
            inputProps: {
              max: elm.dueAmount,
              min: 0,
            },
          }}
          onChange={(e) =>
            handleReceiveAmountChange(i, e.target.value, elm.dueAmount)
          }
        />
      ),
    },
    {
      label: "Due Amount",
      render: (elm) => {
        return elm.dueAmount - (elm.creditAmount ?? elm.dueAmount);
      },
    },
  ];
  const formik = useFormik({
    initialValues: {
      vendor: null,
      platform: null,
      expenseType: null,
      incomeType: null,
      amount: "",
      transaction_date: "",
      description: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      setSubmitting(true);
      const arr = [];
      if (isDebit === transactionType?.credit.toLocaleLowerCase()) {
        if (type === "platform" || type === "partyBillPay") {
          let value = invoiceData.reduce((total, val) => {
            if ((val.creditAmount ?? val.dueAmount) !== 0) {
              arr.push({
                invoiceId: val._id,
                creditAmount: val.creditAmount ?? val.dueAmount,
              });
            }
            return total + (val.creditAmount ?? val.dueAmount);
          }, 0);
          if (value !== +values.amount) {
            errorMsg(`Fillable amount dd is ${value}`);
            return;
          }
        } else {
        }
      } else {
        if (type === "bill") {
          let value = billData.reduce((total, val) => {
            if ((val.debitAmount ?? val.pendingAmount) !== 0) {
              arr.push({
                billId: val._id,
                paidAmount: val.debitAmount ?? val.pendingAmount,
              });
            }
            return total + (val.debitAmount ?? val.pendingAmount);
          }, 0);
          if (value !== +values.amount) {
            errorMsg(`Fillable amount  fgggis ${value}`);
            return;
          }
        }
      }
      const newEntryBookData = {
        bookType: pathname === CASH ? "cash" : "cheque",
        transactionType: isDebit,
        paymentOf:
          isDebit === transactionType?.credit.toLocaleLowerCase()
            ? type === "platform" || type === "partyBillPay"
              ? "invoice"
              : type
            : type,
        transactionDate: values?.transaction_date,
      };

      if (isDebit === transactionType?.credit.toLocaleLowerCase()) {
        if (type === "platform") {
          newEntryBookData.platformId = values?.platform?._id;
          newEntryBookData.invoiceDetails = arr;
        } else if (type === "partyBillPay") {
          newEntryBookData.vendorId = values?.vendor?._id;
          newEntryBookData.invoiceDetails = arr;
        } else {
          newEntryBookData.vendorId = values?.vendor?._id;
          newEntryBookData.incomeTypeId = values?.incomeType?._id;
          newEntryBookData.note = values?.description;
          newEntryBookData.creditAmount = +values?.amount;
        }
      } else {
        newEntryBookData.note = values?.description;
        if (type === "bill") {
          newEntryBookData.vendorId = values?.vendor?._id;
          newEntryBookData.billDetails = arr;
        } else {
          newEntryBookData.expenseTypeId = values?.expenseType?._id;
        }
        newEntryBookData.debitAmount = +values.amount;
      }

      const response = await dispatch(
        createEntryBook(newEntryBookData)
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        resetForm();
        navigate(pathname === CASH ? CASH_BOOK : CHEQUE_BOOK);
      } else {
        errorMsg(response.data?.responseMessage);
      }
      setSubmitting(false);
    },
  });
  const clientHandleChange = (value) => {
    setFieldValue("vendor", value);
    if (value?._id) {
      dispatch(getBillList({ vendorId: value?._id }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            setBillData(response?.data?.data?.billList);
          }
        });
    } else {
      setBillData([]);
    }
  };
  const platformHandleChange = (value) => {
    setFieldValue("platform", value);
    if (value?._id) {
      dispatch(getSalesInvoiceList({ platformId: value?._id }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            setInvoiceData(response?.data?.data?.invoiceList);
          }
        });
    } else {
      setInvoiceData([]);
    }
  };

  const partyHandleChange = (value) => {
    setFieldValue("vendor", value);
    if (value?._id) {
      dispatch(getSalesInvoiceList({ vendorId: value?._id }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            setInvoiceData(response?.data?.data?.invoiceList);
          }
        });
    } else {
      setInvoiceData([]);
    }
  };

  useEffect(() => {
    dispatch(
      getVendorList(
        isDebit === transactionType?.debit.toLocaleLowerCase()
          ? {}
          : { isParty: 1 }
      )
    );
    dispatch(getExpenseTypeList());
    dispatch(getIncomeTypeList());
    dispatch(getPlatformList());
  }, [dispatch, isDebit]);

  useEffect(() => {
    let value = 0;
    if (isDebit === transactionType?.debit.toLocaleLowerCase()) {
      billData.forEach((val) => {
        value += val.debitAmount ?? val.pendingAmount;
      });
    } else {
      invoiceData.forEach((val) => {
        value += val.creditAmount ?? val.dueAmount;
      });
    }
    setFieldValue("amount", value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData, billData]);

  const { setFieldValue, values, handleSubmit, errors, touched } = formik;

  return (
    <Card>
      <CardHeader
        title={`${isDebit.charAt(0).toLocaleUpperCase() + isDebit.slice(1)} ${
          pathname === CASH ? cash : cheque
        }`}
      />
      <CardContent>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {isDebit === transactionType?.debit.toLocaleLowerCase() && (
                <Grid item md={6} xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">
                      Payment Type*
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={type}
                      label="Payment Type*"
                      onChange={(e) => {
                        setType(e.target.value);
                        setFieldValue("vendor", null);
                      }}
                    >
                      <MenuItem value={"expense"}>Expense</MenuItem>
                      <MenuItem value={"bill"}>Bill</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {isDebit !== transactionType?.debit.toLocaleLowerCase() && (
                <Grid item md={6} xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">
                      Payment Type*
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={type}
                      label="Payment Type*"
                      onChange={(e) => {
                        setType(e.target.value);
                        setFieldValue("vendor", null);
                        if (e.target.value === "party")
                          dispatch(getVendorList({ isParty: 1 }));
                        if (e.target.value === "partyBillPay")
                          dispatch(getVendorList({}));
                      }}
                    >
                      <MenuItem value={"platform"}>Platform</MenuItem>
                      <MenuItem value={"partyBillPay"}>Party</MenuItem>
                      <MenuItem value={"party"}>Party (Income Type)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {(type === "party" || type === "partyBillPay") && (
                <Grid item md={6} xs={12}>
                  <AutoComplete
                    options={vendorData.list}
                    label="Vendor*"
                    size="large"
                    value={values?.vendor}
                    getOptionLabel={(option) => option.name}
                    name="vendor"
                    onChange={(evt, value) =>
                      type === "partyBillPay"
                        ? partyHandleChange(value)
                        : setFieldValue("vendor", value)
                    }
                    error={touched?.vendor && Boolean(errors?.vendor)}
                    helperText={touched?.vendor && errors?.vendor}
                  />
                </Grid>
              )}

              <Grid item md={6} xs={12}>
                {isDebit === transactionType?.debit.toLocaleLowerCase() ? (
                  type === "bill" ? (
                    <AutoComplete
                      options={vendorData.list}
                      label="Vendor*"
                      size="large"
                      value={values?.vendor}
                      getOptionLabel={(option) => option.name}
                      name="vendor"
                      onChange={(evt, value) => clientHandleChange(value)}
                      error={touched?.vendor && Boolean(errors?.vendor)}
                      helperText={touched?.vendor && errors?.vendor}
                    />
                  ) : (
                    <AutoComplete
                      options={expenseTypeData.list}
                      label="Expense Type*"
                      size="large"
                      value={values?.expenseType}
                      getOptionLabel={(option) => option.name}
                      name="expenseType"
                      onChange={(evt, value) => {
                        setFieldValue("expenseType", value);
                        setFieldValue("vendor", null);
                      }}
                      error={
                        touched?.expenseType && Boolean(errors?.expenseType)
                      }
                      helperText={touched?.expenseType && errors?.expenseType}
                    />
                  )
                ) : type === "platform" ? (
                  <AutoComplete
                    options={platformData.list}
                    label="Platform*"
                    size="large"
                    getOptionLabel={(option) => option.name}
                    name="platform"
                    onChange={(evt, value) => platformHandleChange(value)}
                    error={touched?.platform && Boolean(errors?.platform)}
                    helperText={touched?.platform && errors?.platform}
                  />
                ) : type === "party" ? (
                  <>
                    <AutoComplete
                      options={incomeTypeData.list}
                      label="Income Type*"
                      size="large"
                      value={values?.incomeType}
                      getOptionLabel={(option) => option.name}
                      name="incomeType"
                      onChange={(evt, value) => {
                        setFieldValue("incomeType", value);
                      }}
                      error={touched?.incomeType && Boolean(errors?.incomeType)}
                      helperText={touched?.incomeType && errors?.incomeType}
                    />
                  </>
                ) : (
                  <JumboTextField
                    type="date"
                    label={"Transaction Date"}
                    name="transaction_date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={values?.transaction_date}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12}>
                <JumboTextField
                  label={"Amount"}
                  name="amount"
                  type="number"
                  fullWidth
                  required
                  value={values?.amount}
                />
              </Grid>
              {type !== "partyBillPay" && (
                <Grid item md={6} xs={12}>
                  <JumboTextField
                    type="date"
                    label={"Transaction Date"}
                    name="transaction_date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={values?.transaction_date}
                  />
                </Grid>
              )}

              {isDebit !== transactionType?.credit.toLocaleLowerCase() &&
                type === "expense" && (
                  <Grid item md={12} xs={12}>
                    <JumboTextField
                      label={"Description*"}
                      name="description"
                      fullWidth
                      value={values?.description}
                    />
                  </Grid>
                )}
              {isDebit === transactionType?.credit.toLocaleLowerCase() &&
                type === "party" && (
                  <Grid item md={12} xs={12}>
                    <JumboTextField
                      label={"Description*"}
                      name="description"
                      fullWidth
                      value={values?.description}
                    />
                  </Grid>
                )}
            </Grid>

            {values?.vendor &&
              type !== "party" &&
              isDebit === transactionType?.debit.toLocaleLowerCase() && (
                <Grid item sm={12} mt={3}>
                  <Paper>
                    <Table columns={column} rows={billData} />
                  </Paper>
                </Grid>
              )}
            {(values?.platform || values?.vendor) &&
              type !== "party" &&
              isDebit !== transactionType?.debit.toLocaleLowerCase() && (
                <Grid item sm={12} mt={3}>
                  <Paper>
                    <Table columns={columnInvoice} rows={invoiceData} />
                  </Paper>
                </Grid>
              )}

            <Grid item sm={12} className={classes?.buttonContainer}>
              <Button
                buttonLabel={"Save"}
                sx={{ marginRight: "10px" }}
                startIcon={<Save />}
                variant="outlined"
              />
              <Button
                color={"secondary"}
                buttonLabel={"Close"}
                variant="outlined"
                startIcon={<Close />}
                onClick={() =>
                  navigate(pathname === CASH ? CASH_BOOK : CHEQUE_BOOK)
                }
              />
            </Grid>
          </Form>
        </FormikProvider>
      </CardContent>
    </Card>
  );
};

export default CreditAndDebit;
